import {
  Card,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { ValidationErrors, Validator } from 'fluentvalidation-ts';
import React, { useEffect } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';

import {
  CustomSelect,
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import {
  ThemedCopyIcon,
  ThemedRefreshIcon,
  ThemedVisibilityIcon,
  ThemedVisibilityOffIcon,
} from '../../../../Common/Components/Icons';
import { showToast } from '../../../../lib/toast';
import { generateAlphaNumericPassword } from '../../SqlServer/Common/util';
import { CardWrapper } from '../../Common/CommonComponents';
import { ValidateEmails } from '../../EmailAdministration/Common/Common';
import { customerService } from '../CustomerService';
import {
  AvailableIdentityProviders,
  NewCustomer,
} from '../Model/CustomerModel';
import { EnvOption } from '../Model/CustomerProps';

/* -------------------- Types -------------------- */

type OpsType = 'Create' | 'Edit';

interface CustomerFormProps {
  groupNames: string[];
  envOptions: EnvOption[];
  onFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  customer: NewCustomer;
  errors: ValidationErrors<NewCustomer>;
  setErrors: React.Dispatch<
    React.SetStateAction<ValidationErrors<NewCustomer>>
  >;
  ops: OpsType;
  updateUserAdminPassword: (password: string) => void;
}

/* -------------------- Validator -------------------- */

class CustomerValidator extends Validator<NewCustomer> {
  constructor() {
    super();

    this.ruleFor('Subdomain')
      .notEmpty()
      .withMessage('Subdomain cannot be empty')
      .notNull()
      .withMessage('Subdomain cannot be null')
      .matches(/^[a-zA-Z0-9-]+$/)
      .withMessage(
        'Subdomain can only contain alphanumeric characters and hyphens',
      )
      .length(2, 63)
      .withMessage('Subdomain must be between 2 and 63 characters long')
      .matches(/^[^-].*[^-]$/)
      .withMessage('Subdomain cannot start or end with a hyphen')
      .matches(/^(?!.*--).*$/)
      .withMessage('Subdomain cannot contain consecutive hyphens');

    this.ruleFor('CustomerName')
      .notEmpty()
      .withMessage('Customer Name cannot be empty')
      .notNull()
      .withMessage('Customer Name cannot be empty');

    this.ruleFor('GroupName')
      .notEmpty()
      .withMessage('Group Name cannot be empty')
      .notNull()
      .withMessage('Group Name cannot be empty');

    this.ruleFor('AdminUser')
      .notEmpty()
      .withMessage('Admin User Email cannot be empty')
      .emailAddress()
      .withMessage('Should be Email Address')
      .notNull()
      .withMessage('Admin User Name cannot be empty');

    this.ruleFor('AdminUserPassword')
      .notEmpty()
      .withMessage('Admin User Password cannot be empty')
      .notNull()
      .withMessage('Admin User Password cannot be empty')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{4,20}$/)
      .withMessage(
        'Password must contain at least one lowercase letter, one uppercase letter, and one digit, and be between 4 and 20 characters long',
      );

    this.ruleFor('AzureADAppClientID')
      .notEmpty()
      .withMessage('Azure AD App Client ID cannot be empty')
      .notNull()
      .withMessage('Azure AD App Client ID cannot be empty');

    this.ruleFor('AuthProviderName')
      .notEmpty()
      .withMessage('Auth Provider Name cannot be empty')
      .notNull()
      .withMessage('Auth Provider Name cannot be empty');

    this.ruleFor('WSFederationUrl')
      .notEmpty()
      .withMessage('Auth Metadata URL cannot be empty')
      .notNull()
      .withMessage('Auth Metadata URL cannot be empty');

    this.ruleFor('InputClaimType')
      .notEmpty()
      .withMessage('Input Claim Type cannot be empty')
      .notNull()
      .withMessage('Input Claim Type cannot be empty');

    this.ruleFor('IdentityProviders')
      .notEmpty()
      .withMessage('Identity Providers cannot be empty')
      .notNull()
      .withMessage('Identity Providers cannot be empty');

    this.ruleFor('ContactPersons')
      .notEmpty()
      .withMessage('Contact Email cannot be empty')
      .must((value: unknown) => ValidateEmails(value))
      .withMessage('Each email should be a valid email address');

    this.ruleFor('AdminUserName')
      .notEmpty()
      .withMessage('Admin User Name cannot be empty')
      .notNull()
      .withMessage('Admin User Name cannot be empty');
  }
}

/* -------------------- Helpers -------------------- */

const customerValidator = new CustomerValidator();

function checkSubdomainExists(subdomain: string) {
  return customerService.validateSubdomain(subdomain);
}

/* -------------------- Component -------------------- */

const CustomerForm: React.FC<CustomerFormProps> = ({
  groupNames,
  envOptions,
  customer,
  errors,
  onFieldChange,
  setErrors,
  ops,
  updateUserAdminPassword,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('customer');
  const disableControls = ops === 'Edit';

  const [adminPasswordVisible, setAdminPasswordVisible] = React.useState(false);

  const validate = () => {
    setErrors(customerValidator.validate(customer));
  };

  const validateSubdomain = () => {
    if (customer.Subdomain) {
      const exists = checkSubdomainExists(customer.Subdomain);
      setErrors((prev) => ({
        ...prev,
        Subdomain: exists ? 'This subdomain already exists' : '',
      }));
    }
    validate();
  };

  const handlePasswordGenerate = () => {
    updateUserAdminPassword(generateAlphaNumericPassword(10));
    showToast({
      title: t('admin_user.password.generate_success'),
      type: 'success',
    });
  };

  useEffect(() => {
    validateSubdomain();
  }, [customer]);

  return (
    <Grid container spacing={3}>
      {/* -------------------- Left Column -------------------- */}
      <Grid item xs={12} md={6}>
        <CardWrapper>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <FormRow label="Subdomain">
            <CustomTextField
              disabled={disableControls}
              error={!!errors.Subdomain}
              helperText={errors.Subdomain}
              value={customer.Subdomain}
              placeholder="Subdomain"
              name="Subdomain"
              onChange={onFieldChange}
              onBlur={validateSubdomain}
            />
          </FormRow>

          <FormRow label="Customer Name">
            <CustomTextField
              error={!!errors.CustomerName}
              helperText={errors.CustomerName}
              value={customer.CustomerName}
              placeholder="Customer Name"
              name="CustomerName"
              onChange={onFieldChange}
              onBlur={validate}
            />
          </FormRow>

          <FormRow label="Enterprise Number">
            <CustomTextField
              value={customer.EnterpriseNumber}
              placeholder="Enterprise Number"
              name="EnterpriseNumber"
              onChange={onFieldChange}
            />
          </FormRow>

          <FormRow label="Contact Email">
            <CustomTextField
              error={!!errors.ContactPersons}
              helperText={errors.ContactPersons}
              value={customer.ContactPersons}
              placeholder="Contact Email"
              name="ContactPersons"
              onChange={onFieldChange}
              onBlur={validate}
            />
          </FormRow>
        </CardWrapper>

        <CardWrapper sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Group Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <FormRow label="Group Name">
            <CustomSelect
              error={!!errors.GroupName}
              helperText={errors.GroupName}
              value={customer.GroupName ?? ''}
              placeholder="Group Name"
              name="GroupName"
              onChange={onFieldChange}
              onBlur={validate}
              select
            >
              {groupNames.map((group) => (
                <MenuItem key={group} value={group}>
                  {group}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormRow>

          <FormRow label="Environment Type">
            <CustomSelect
              error={!!errors.EnvType}
              helperText={errors.EnvType}
              value={customer.EnvType ?? ''}
              placeholder="Environment Type"
              name="EnvType"
              onChange={onFieldChange}
              onBlur={validate}
              select
            >
              {envOptions.map((env) => (
                <MenuItem key={env.value} value={env.value}>
                  {env.name}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormRow>
        </CardWrapper>
      </Grid>

      {/* -------------------- Right Column -------------------- */}
      <Grid item xs={12} md={6}>
        <Card
          elevation={3}
          sx={{
            p: 3,
            borderRadius: '12px',
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Authentication
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <FormRow label="Enable ProArc Login">
            <Checkbox
              checked={customer.EnableProArcLogin ?? false}
              name="EnableProArcLogin"
              onChange={onFieldChange}
              onBlur={validate}
            />
          </FormRow>

          <FormRow label="Enable External Identity Provider">
            <Checkbox
              checked={customer.ExternalIdentityProvider ?? false}
              name="ExternalIdentityProvider"
              onChange={onFieldChange}
              onBlur={validate}
            />
          </FormRow>

          <FormRow label="Admin Email">
            <CustomTextField
              error={!!errors.AdminUser}
              helperText={errors.AdminUser}
              value={customer.AdminUser}
              placeholder="Admin User"
              name="AdminUser"
              onChange={onFieldChange}
              onBlur={validate}
            />
          </FormRow>

          <FormRow label="Admin User Name">
            <CustomTextField
              error={!!errors.AdminUserName}
              helperText={errors.AdminUserName}
              value={customer.AdminUserName}
              placeholder="Admin User Name"
              name="AdminUserName"
              onChange={onFieldChange}
              onBlur={validate}
            />
          </FormRow>

          <FormRow label="Admin User Password">
            <CustomTextField
              type={adminPasswordVisible ? 'text' : 'password'}
              error={!!errors.AdminUserPassword}
              helperText={errors.AdminUserPassword}
              value={customer.AdminUserPassword}
              placeholder="Admin User Password"
              name="AdminUserPassword"
              onChange={onFieldChange}
              onBlur={validate}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setAdminPasswordVisible((v) => !v)}
                    >
                      {adminPasswordVisible ? (
                        <ThemedVisibilityOffIcon fontSize="small" />
                      ) : (
                        <ThemedVisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                    <CopyToClipboard
                      text={customer.AdminUserPassword ?? ''}
                      onCopy={() =>
                        showToast({
                          title: t('admin_user.password.copy_success'),
                          type: 'success',
                        })
                      }
                    >
                      <IconButton size="small">
                        <ThemedCopyIcon fontSize="small" />
                      </IconButton>
                    </CopyToClipboard>
                    <IconButton size="small" onClick={handlePasswordGenerate}>
                      <ThemedRefreshIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormRow>

          {customer.ExternalIdentityProvider && (
            <>
              <FormRow label="Identity Provider">
                <CustomSelect
                  error={!!errors.IdentityProviders}
                  helperText={errors.IdentityProviders}
                  value={customer.IdentityProviders ?? ''}
                  placeholder="Identity Provider"
                  name="IdentityProviders"
                  onChange={onFieldChange}
                  onBlur={validate}
                  select
                >
                  {Object.entries(AvailableIdentityProviders).map(
                    ([key, value]) => (
                      <MenuItem key={key} value={value}>
                        {value}
                      </MenuItem>
                    ),
                  )}
                </CustomSelect>
              </FormRow>

              <FormRow label="Input Claim Type">
                <CustomTextField
                  error={!!errors.InputClaimType}
                  helperText={errors.InputClaimType}
                  value={customer.InputClaimType}
                  placeholder="Input Claim Type"
                  name="InputClaimType"
                  onChange={onFieldChange}
                  onBlur={validate}
                />
              </FormRow>

              <FormRow label="Auth Client ID">
                <CustomTextField
                  error={!!errors.AzureADAppClientID}
                  helperText={errors.AzureADAppClientID}
                  value={customer.AzureADAppClientID}
                  placeholder="Auth Client ID"
                  name="AzureADAppClientID"
                  onChange={onFieldChange}
                  onBlur={validate}
                />
              </FormRow>

              <FormRow label="Auth Metadata URL">
                <CustomTextField
                  error={!!errors.WSFederationUrl}
                  helperText={errors.WSFederationUrl}
                  value={customer.WSFederationUrl}
                  placeholder="Auth Metadata URL"
                  name="WSFederationUrl"
                  onChange={onFieldChange}
                  onBlur={validate}
                />
              </FormRow>

              <FormRow label="Auth Provider Name">
                <CustomTextField
                  error={!!errors.AuthProviderName}
                  helperText={errors.AuthProviderName}
                  value={customer.AuthProviderName}
                  placeholder="Auth Provider Name"
                  name="AuthProviderName"
                  onChange={onFieldChange}
                  onBlur={validate}
                />
              </FormRow>
            </>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default CustomerForm;

/* -------------------- FormRow helper -------------------- */

interface FormRowProps {
  label: string;
  children: React.ReactNode;
}

function FormRow({ label, children }: FormRowProps) {
  return (
    <Grid container spacing={2} alignItems="center" mt={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="body1">{label}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        {children}
      </Grid>
    </Grid>
  );
}
